import { createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { STATUS_STYLE } from "@/constants/crm";

export const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function StatusBadge({ value, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${STATUS_STYLE[value] || "border-transparent bg-zinc-100 text-zinc-600"}`}>
      {value || "—"}
    </span>
  );
}

export const EmbeddedContext = createContext(false);

export function PageHeader({ num, title, subtitle, children }) {
  const embedded = useContext(EmbeddedContext);
  if (embedded) {
    return (
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <h2 data-testid="section-title" className="text-lg font-bold tracking-tight text-zinc-900">{title}</h2>
          {subtitle && <p className="mt-0.5 max-w-2xl text-sm text-zinc-500">{subtitle}</p>}
        </div>
        {children && <div className="flex flex-wrap items-center gap-2 md:justify-end">{children}</div>}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up">
      <div className="min-w-0">
        <h1 data-testid="page-title" className="text-2xl font-bold tracking-tight text-zinc-900 sm:text-[28px]">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-zinc-500">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2 md:justify-end">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, testid, tone = "text-zinc-900", icon: Icon, iconClass = "bg-violet-50 text-violet-600" }) {
  return (
    <div data-testid={testid} className="flex min-h-[124px] gap-4 rounded-2xl border border-zinc-100 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-shadow hover:shadow-md fade-up">
      {Icon && <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconClass}`}><Icon className="h-5 w-5" /></span>}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="text-[13px] font-medium text-zinc-500">{label}</div>
        <div className={`mt-1 text-2xl font-bold tracking-tight ${tone}`}>{value}</div>
        {hint && <div className="mt-auto pt-2 text-xs text-zinc-400">{hint}</div>}
      </div>
    </div>
  );
}

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] fade-up ${className}`}>
      {title && (
        <div className="flex min-h-[56px] items-center justify-between gap-3 px-5 pt-4 pb-1">
          <h3 className="text-base font-bold text-zinc-900">{title}</h3>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function PillTabs({ tabs, value, onChange, testid = "tab" }) {
  return (
    <div className="inline-flex max-w-full flex-wrap gap-1 rounded-xl border border-zinc-100 bg-white p-1 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      {tabs.map((t) => {
        const [k, label, count] = Array.isArray(t) ? t : [t, t];
        return (
          <button key={k} data-testid={`${testid}-${slug(k)}`} onClick={() => onChange(k)}
            className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-colors ${value === k ? "bg-violet-50 text-violet-700" : "text-zinc-500 hover:text-zinc-800"}`}>
            {label}{count !== undefined && <span className="ml-1.5 font-mono text-[10px] text-zinc-400">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export const selectCls = "h-9 rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-700 transition-colors hover:border-zinc-300 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-500/10";

export function NativeSelect({ value, onChange, options, testid, placeholder, className = "" }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${selectCls} ${className}`}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        return <option key={v} value={v}>{typeof o === "string" ? o : o.label}</option>;
      })}
    </select>
  );
}

export function ViewAll({ to, label = "View all", testid }) {
  return <Link to={to} data-testid={testid} className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">{label}<ArrowRight className="h-3.5 w-3.5" /></Link>;
}

export function Empty({ text = "No records yet." }) {
  return <div className="py-10 text-center text-sm text-zinc-400">{text}</div>;
}
